import React, { useState, useEffect, useCallback } from "react";
import {
  Card, CardBody, Col, Container, Input, Label, Row,
  Button, Table, FormGroup, Modal, ModalHeader, ModalBody,
} from "reactstrap";
import { getPayments, getPaymentById, exportPaymentsSummary, exportPaymentsDetails } from "../../components/Api/GmsPayment";
import { searchSuppliers } from "../../components/Api/GmsSupplier";
import Select from "react-select";
import { Eye, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { hasPagePermissionWithAdmin } from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";

const PAGE_ROUTE = "/GmsPaymentsList";

const selectStyles = {
  control: (p, s) => ({ ...p, minHeight: "36px", height: "36px", borderColor: s.isFocused ? "#556ee6" : "#ced4da", boxShadow: s.isFocused ? "0 0 0 0.2rem rgba(85,110,230,.25)" : "none" }),
  valueContainer: (p) => ({ ...p, height: "34px", padding: "0 8px" }),
  indicatorsContainer: (p) => ({ ...p, height: "34px" }),
  menu: (p) => ({ ...p, zIndex: 9999 }),
  option: (p, s) => ({ ...p, backgroundColor: s.isSelected ? "#556ee6" : s.isFocused ? "#f8f9fa" : "white", color: s.isSelected ? "white" : "#495057" }),
};

const emptyFilter = {
  BusinessId: null,
  BranchId: null,
  PayeeId: null,
  FromDate: null,
  ToDate: null,
  Page: 1,
  PageSize: 10,
};

const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

const fmt = (n) => parseFloat(n || 0).toFixed(2);
const fmtDate = (d) => d ? formatDateForDisplay(d, "date") : "-";

export default function PaymentsListPage() {
  const [filter, setFilter] = useState(emptyFilter);
  const [payments, setPayments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewLoading, setViewLoading] = useState(null);
  const [modal, setModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierTimer, setSupplierTimer] = useState(null);
  const [exportingSummary, setExportingSummary] = useState(false);
  const [exportingRowId, setExportingRowId] = useState(null);

  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
  const hasPermission = hasPagePermissionWithAdmin(permissions, PAGE_ROUTE);
  const session = getUserSession();

  useEffect(() => {
    if (!hasPermission) { window.location.href = "/Dashboard"; return; }
    const initialFilter = { ...emptyFilter };
    if (session.companyId) initialFilter.BusinessId = session.companyId;
    if (session.branchId) initialFilter.BranchId = session.branchId;
    setFilter(initialFilter);
  }, [hasPermission]);

  useEffect(() => {
    if (hasPermission) loadPayments();
  }, [filter.Page, filter.PageSize]);

  const loadPayments = async (overrideFilter = null) => {
    try {
      setLoading(true);
      const f = overrideFilter || filter;
      const payload = { Page: f.Page, PageSize: f.PageSize, PayeeType: "sup" };
      if (f.BusinessId) payload.BusinessId = f.BusinessId;
      if (f.BranchId) payload.BranchId = f.BranchId;
      if (f.PayeeId) payload.PayeeId = f.PayeeId;
      if (f.FromDate) payload.FromDate = formatDateForAPI(f.FromDate, false);
      if (f.ToDate) payload.ToDate = formatDateForAPI(f.ToDate, false);

      const res = await getPayments(payload);
      setPayments(res.payments || []);
      setTotalCount(res.totalCount || 0);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to load payments.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    const f = { ...filter, Page: 1 };
    setFilter(f);
    loadPayments(f);
  };

  const handleClearFilter = () => {
    const f = { ...emptyFilter };
    if (session.companyId) f.BusinessId = session.companyId;
    if (session.branchId) f.BranchId = session.branchId;
    setFilter(f);
    setSelectedSupplier(null);
    loadPayments(f);
  };

  const handlePageChange = (newPage) => setFilter(p => ({ ...p, Page: newPage }));

  const handleView = async (p) => {
    const id = p.payment_Id || p.paymentId;
    try {
      setViewLoading(id);
      const detail = await getPaymentById(id);
      setSelectedPayment({ ...p, ...detail });
      setModal(true);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to load payment details.", "error");
    } finally {
      setViewLoading(null);
    }
  };

  const handleSupplierSearch = (val) => {
    clearTimeout(supplierTimer);
    if (!val || val.length < 2) { setSupplierOptions([]); return; }
    const t = setTimeout(async () => {
      try {
        setSupplierLoading(true);
        const res = await searchSuppliers(val, 1, 20);
        const items = res?.items || res || [];
        setSupplierOptions(items.map(s => ({ value: s.supplier_Id, label: `${s.supplier_NameEn || ""} (${s.supplier_Code || ""})` })));
      } catch (e) { console.error(e); }
      finally { setSupplierLoading(false); }
    }, 400);
    setSupplierTimer(t);
  };

  const buildExportFilter = () => {
    const f = { PayeeType: "sup" };
    if (filter.BusinessId) f.BusinessId = filter.BusinessId;
    if (filter.BranchId) f.BranchId = filter.BranchId;
    if (filter.PayeeId) f.PayeeId = filter.PayeeId;
    if (filter.FromDate) f.FromDate = formatDateForAPI(filter.FromDate, false);
    if (filter.ToDate) f.ToDate = formatDateForAPI(filter.ToDate, false);
    return f;
  };

  const handleExportSummary = async () => {
    try {
      setExportingSummary(true);
      await exportPaymentsSummary(buildExportFilter());
    } catch (e) { Swal.fire("Error", "Failed to export summary.", "error"); }
    finally { setExportingSummary(false); }
  };

  const handleExportRowDetails = async (payment) => {
    const id = payment.paymentId || payment.payment_Id;
    try {
      setExportingRowId(id);
      await exportPaymentsDetails({ PaymentId: id, PayeeType: "sup" });
    } catch (e) { Swal.fire("Error", "Failed to export payment details.", "error"); }
    finally { setExportingRowId(null); }
  };

  const totalPages = Math.ceil(totalCount / filter.PageSize);

  if (!hasPermission) return null;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Payments List" breadcrumbItem="Payments List" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  {/* Header toolbar */}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">
                      Payments List <span className="text-muted fs-6">({totalCount} records)</span>
                    </h5>
                    <div className="d-flex gap-2">
                      <Button color="success" size="sm" onClick={handleExportSummary} disabled={exportingSummary}>
                        {exportingSummary ? <ClipLoader size={14} color="#fff" className="me-1" /> : <i className="mdi mdi-download me-1" />}
                        Export Summary
                      </Button>
                      <Button color="primary" size="sm" onClick={() => setShowFilters(p => !p)}>
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </Button>
                    </div>
                  </div>

                  {/* Filters */}
                  {showFilters && (
                    <Row className="mb-3 g-2">
                      <Col md={3}>
                        <FormGroup className="mb-0">
                          <Label>Supplier</Label>
                          <Select
                            value={selectedSupplier}
                            onChange={opt => { setSelectedSupplier(opt); setFilter(p => ({ ...p, PayeeId: opt?.value || null })); }}
                            onInputChange={handleSupplierSearch}
                            options={supplierOptions}
                            isLoading={supplierLoading}
                            isClearable
                            placeholder="Search supplier..."
                            noOptionsMessage={({ inputValue }) => inputValue?.length >= 2 ? "No suppliers found" : "Type to search..."}
                            filterOption={() => true}
                            styles={selectStyles}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup className="mb-0">
                          <Label>From Date</Label>
                          <DatePicker
                            selected={filter.FromDate}
                            onChange={d => setFilter(p => ({ ...p, FromDate: d }))}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            isClearable
                            placeholderText="From date"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup className="mb-0">
                          <Label>To Date</Label>
                          <DatePicker
                            selected={filter.ToDate}
                            onChange={d => setFilter(p => ({ ...p, ToDate: d }))}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            isClearable
                            placeholderText="To date"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup className="mb-0">
                          <Label className="d-block" style={{ visibility: "hidden" }}>Actions</Label>
                          <div className="d-flex gap-2">
                            <Button color="primary" onClick={handleApplyFilter} style={{ height: "36px", padding: "0 16px" }}>Apply</Button>
                            <Button color="secondary" outline onClick={handleClearFilter} style={{ height: "36px", padding: "0 16px" }}>Clear</Button>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  {/* Table */}
                  {loading ? (
                    <div className="text-center py-5">
                      <ClipLoader color="#556ee6" size={48} />
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table bordered hover size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Payment No</th>
                              <th>Date</th>
                              <th>Supplier</th>
                              <th className="text-end">Net Amount</th>
                              <th className="text-end">Cash</th>
                              <th className="text-end">Bank/Online</th>
                              <th>Ref No</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payments.length === 0 ? (
                              <tr>
                                <td colSpan={9} className="text-center text-muted py-4">
                                  No payments found.
                                </td>
                              </tr>
                            ) : (
                              payments.map((p, idx) => {
                                const id = p.payment_Id || p.paymentId;
                                const payNo = p.payment_No || p.paymentNo || "-";
                                const payDate = p.payment_Date || p.paymentDate;
                                const supplierName = p.supplier_NameEn || p.supplierNameEn || "-";
                                const netAmt = p.net_Amount ?? p.netAmount ?? 0;
                                const cashAmt = p.cash_Amount ?? p.cashAmount ?? 0;
                                const cashlessAmt = p.cashless_Amount ?? p.cashlessAmount ?? 0;
                                const refNo = p.payment_Reference_No || p.paymentReferenceNo || "-";

                                return (
                                  <tr key={id}>
                                    <td>{(filter.Page - 1) * filter.PageSize + idx + 1}</td>
                                    <td><strong>{payNo}</strong></td>
                                    <td>{fmtDate(payDate)}</td>
                                    <td>{supplierName}</td>
                                    <td className="text-end fw-bold text-primary">{fmt(netAmt)}</td>
                                    <td className="text-end">{fmt(cashAmt)}</td>
                                    <td className="text-end">{fmt(cashlessAmt)}</td>
                                    <td>{refNo}</td>
                                    <td>
                                      <div className="d-flex gap-1">
                                        <Button
                                          color="primary"
                                          size="sm"
                                          onClick={() => handleView(p)}
                                          disabled={viewLoading === id}
                                          title="View Details"
                                        >
                                          {viewLoading === id ? <ClipLoader size={14} color="#fff" /> : <Eye size={14} />}
                                        </Button>
                                        <Button
                                          color="success"
                                          size="sm"
                                          onClick={() => handleExportRowDetails(p)}
                                          disabled={exportingRowId === id}
                                          title="Export Details"
                                        >
                                          {exportingRowId === id ? <ClipLoader size={14} color="#fff" /> : <Download size={14} />}
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                          {payments.length > 0 && (
                            <tfoot className="table-light">
                              <tr>
                                <td colSpan={4} className="text-end fw-bold">Totals:</td>
                                <td className="text-end fw-bold text-primary">{fmt(payments.reduce((s, p) => s + (p.net_Amount ?? p.netAmount ?? 0), 0))}</td>
                                <td className="text-end fw-bold">{fmt(payments.reduce((s, p) => s + (p.cash_Amount ?? p.cashAmount ?? 0), 0))}</td>
                                <td className="text-end fw-bold">{fmt(payments.reduce((s, p) => s + (p.cashless_Amount ?? p.cashlessAmount ?? 0), 0))}</td>
                                <td colSpan={2}></td>
                              </tr>
                            </tfoot>
                          )}
                        </Table>
                      </div>

                      {/* Pagination */}
                      {totalCount > filter.PageSize && (
                        <div className="d-flex justify-content-between align-items-center mt-3 px-1">
                          <span className="text-muted" style={{ fontSize: 13 }}>
                            Showing <strong>{(filter.Page - 1) * filter.PageSize + 1}</strong> to <strong>{Math.min(filter.Page * filter.PageSize, totalCount)}</strong> of <strong>{totalCount}</strong>
                          </span>
                          <div className="d-flex gap-1">
                            <Button color="light" size="sm" disabled={filter.Page === 1} onClick={() => handlePageChange(filter.Page - 1)}>
                              <i className="mdi mdi-chevron-left" />
                            </Button>
                            {[...Array(totalPages)].map((_, i) => {
                              const pg = i + 1;
                              if (totalPages > 7 && (pg > 3 && pg < totalPages - 1 && Math.abs(pg - filter.Page) > 1)) {
                                return pg === 4 ? <span key={pg} className="px-1 align-self-center text-muted">…</span> : null;
                              }
                              return (
                                <Button
                                  key={pg}
                                  size="sm"
                                  color={filter.Page === pg ? "primary" : "light"}
                                  onClick={() => handlePageChange(pg)}
                                  style={{ minWidth: 32 }}
                                >
                                  {pg}
                                </Button>
                              );
                            })}
                            <Button color="light" size="sm" disabled={filter.Page >= totalPages} onClick={() => handlePageChange(filter.Page + 1)}>
                              <i className="mdi mdi-chevron-right" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* View Payment Detail Modal */}
        <PaymentDetailModal
          isOpen={modal}
          toggle={() => setModal(false)}
          payment={selectedPayment}
          fmt={fmt}
          fmtDate={fmtDate}
        />
      </div>
    </React.Fragment>
  );
}

// =============================================
// Payment Detail Modal
// =============================================
function PaymentDetailModal({ isOpen, toggle, payment, fmt, fmtDate }) {
  if (!payment) return null;

  const payNo = payment.payment_No || payment.paymentNo || "-";
  const payDate = payment.payment_Date || payment.paymentDate;
  const supplier = payment.supplier_NameEn || payment.supplierNameEn || "-";
  const business = payment.business_NameEn || payment.businessNameEn || "-";
  const branch = payment.branch_NameEn || payment.branchNameEn || "-";
  const netAmt = payment.net_Amount ?? payment.netAmount ?? 0;
  const cashAmt = payment.cash_Amount ?? payment.cashAmount ?? 0;
  const cashlessAmt = payment.cashless_Amount ?? payment.cashlessAmount ?? 0;
  const refNo = payment.payment_Reference_No || payment.paymentReferenceNo || null;
  const notes = payment.notes || null;

  const details = payment.paymentDetails || payment.PaymentDetails || [];

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Payment Details — <strong>{payNo}</strong>
      </ModalHeader>
      <ModalBody>
        {/* Header info */}
        <Card className="mb-3 border-primary">
          <CardBody>
            <h6 className="card-title text-primary mb-3">Payment Information</h6>
            <Row>
              <Col md={6}>
                <p className="mb-2"><strong>Payment No:</strong> {payNo}</p>
                <p className="mb-2"><strong>Date:</strong> {fmtDate(payDate)}</p>
                <p className="mb-2"><strong>Supplier:</strong> {supplier}</p>
                <p className="mb-2"><strong>Company:</strong> {business}</p>
                <p className="mb-2"><strong>Branch:</strong> {branch}</p>
              </Col>
              <Col md={6}>
                <p className="mb-2"><strong>Net Amount:</strong> <span className="text-primary fw-bold">{fmt(netAmt)}</span></p>
                <p className="mb-2"><strong>Cash Amount:</strong> {fmt(cashAmt)}</p>
                <p className="mb-2"><strong>Bank/Online:</strong> {fmt(cashlessAmt)}</p>
                {refNo && <p className="mb-2"><strong>Reference No:</strong> {refNo}</p>}
                {notes && <p className="mb-2"><strong>Notes:</strong> {notes}</p>}
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Invoice allocation */}
        <Card className="border-warning">
          <CardBody>
            <h6 className="card-title text-warning mb-3">Invoice Allocation</h6>
            {details.length === 0 ? (
              <div className="alert alert-info mb-0">
                No invoice allocation details found. This may be an advance payment.
              </div>
            ) : (
              <div className="table-responsive">
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Invoice Amount</th>
                      <th className="text-end">Paid Amount</th>
                      <th className="text-end">Total Amount</th>
                      <th className="text-end">Settled</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.map((d, i) => {
                      const paid = d.paid_Amount ?? d.paidAmount ?? d.PaidAmount ?? 0;
                      const total = d.total_Amount ?? d.totalAmount ?? d.TotalAmount ?? 0;
                      const invoice = d.invoice_Amount ?? d.invoiceAmount ?? d.InvoiceAmount ?? 0;
                      const settled = d.settled_Amount ?? d.settledAmount ?? d.SettledAmount ?? 0;
                      const isFullyPaid = settled >= total && total > 0;
                      return (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td className="text-end">{fmt(invoice)}</td>
                          <td className="text-end fw-bold text-success">{fmt(paid)}</td>
                          <td className="text-end">{fmt(total)}</td>
                          <td className="text-end">{fmt(settled)}</td>
                          <td>
                            {isFullyPaid
                              ? <span className="badge bg-success">Fully Paid</span>
                              : <span className="badge bg-warning text-dark">Partial</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td colSpan={2} className="text-end fw-bold">Total Allocated:</td>
                      <td className="text-end fw-bold text-success">
                        {fmt(details.reduce((s, d) => s + (d.paid_Amount ?? d.paidAmount ?? d.PaidAmount ?? 0), 0))}
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </ModalBody>
    </Modal>
  );
}
